/**
 * Cross-surface auth state machine — the vocabulary and the sole reducer.
 *
 * ## Why this exists
 *
 * Auth state was 25 independently-evaluated predicates across five surfaces
 * (web app, desktop renderer, Electron main, the Rust daemon, the SDK). Every
 * observed defect was two of them disagreeing: a 401 read as "sign out", an
 * unreadable expiry read as "signed out", a local clock read treated as a global
 * adjudication. The SDK is the only artifact every surface already depends on —
 * and a web-only install has no daemon at all — so the SDK carries the contract
 * and is the sole manufacturer of verdicts.
 *
 * ## The two layers
 *
 * **Layer 1 — `SessionVerdict`.** Adjudicated by the Go backend and NEVER
 * stored. Derived solely from an API response. The backend names the cause of
 * every 401 it adjudicates in `auth_reason` (one mint site,
 * `go/internal/api/auth_middleware.go`), so `classifyVerdict` reads a field —
 * it never matches message text. Matching text is what produced app-shell ADR-7699b's
 * Stytch-prose matching and, later, an infinite sign-in loop.
 *
 * **Layer 2 — `HolderPhase`.** The per-credential-holder state. Identity is
 * NON-OPTIONAL on the variants that have it, so "token but no claims" and
 * "claims but no token" are unrepresentable rather than merely discouraged.
 *
 * ## THE DERIVATION RULE — the whole design in one sentence
 *
 * A holder changes phase ONLY by (a) a local material event it itself caused, or
 * (b) applying a verdict. It NEVER computes a phase from raw material.
 *
 * Two corollaries carry the weight:
 *
 *   - A locally-decoded `exp` may emit `ClockExpiry` (`usable -> degraded`,
 *     RECOVERABLE) and may NEVER reach `NoCredential`. A local clock read is not
 *     a global adjudication.
 *   - Only a `rejected` verdict or an explicit local clear reaches
 *     `NoCredential`. An `absent` verdict (401 `missing_token`) changes nothing
 *     at all: "I sent no credential" says nothing whatsoever about the session.
 *
 * `unknown` is load-bearing for the same reason: a 5xx is NOT an adjudication,
 * so a Stytch blip parks holders in `degraded(unconfirmed)` (material intact,
 * fully recoverable) instead of signing everyone out.
 */

import {
  APIError,
  MissingCredentialError,
  NetworkError,
  RequestCancelledError,
  TimeoutError,
} from '../errors.js';

// ─────────────────────────────────────────────────────────────────────────────
// Layer 1 — SessionVerdict
// ─────────────────────────────────────────────────────────────────────────────

/** Why a `rejected` verdict is terminal. All three are GLOBAL and unrecoverable. */
export type RejectedCause = 'bad_signature' | 'unknown_kid' | 'wrong_issuer';

/**
 * Why a `not_activated` verdict happened. Both arrive as
 * `401 error_code=SESSION_NOT_ACTIVATED`, and the difference is actionable:
 * `missing_account_claim` IS fixed by activating; `malformed_tenancy_claims`
 * means the claim is present but unusable, which activating again will not fix.
 */
export type NotActivatedCause = 'missing_account_claim' | 'malformed_tenancy_claims';

/**
 * Why a response adjudicated nothing.
 *
 * Every value here means the same thing to a holder: back off, CHANGE NOTHING.
 * They are distinguished so telemetry and back-off policy can differ, never so
 * that a holder can be moved on one of them.
 */
export type UnknownVerdictCause =
  /** 5xx. The backend failed to answer, which says nothing about the credential. */
  | 'server_error'
  /** 503 `STYTCH_UNAVAILABLE` — the identity provider is degraded, not the session. */
  | 'provider_unavailable'
  /** The request timed out client-side. */
  | 'timeout'
  /** Network failure, deliberate cancellation, or a non-Error throwable. */
  | 'transport'
  /**
   * The response did not adjudicate the session at all. Two shapes reach here:
   * a non-401 4xx (403/404/409 — the credential passed the auth gate and the
   * failure is about something else), and a 401 that names NO `auth_reason`.
   *
   * The second is not hypothetical: `mapAuthErrorBody`'s handler lane returns
   * 401 `AUTH_FAILED` / `STYTCH_CREATE_ORG_FAILED` / `ADDITIONAL_AUTH_REQUIRED`
   * for a bad BODY credential (a stale handoff code, a spent IST) with no
   * `auth_reason` — those reject the request's payload, not the bearer session.
   * Treating them as `rejected` would sign a user out for typing a bad code,
   * which is precisely the defect class this design removes.
   */
  | 'unadjudicated'
  /** `auth_reason=secret_not_configured` — a SERVER misconfiguration. */
  | 'server_misconfigured'
  /** A 401 naming an `auth_reason` this SDK build does not know. Never guess. */
  | 'unrecognized_reason';

/**
 * What the backend said about the credential that was sent.
 *
 * NEVER stored. It is a fact about one response, consumed immediately by
 * `applyEvent` and discarded.
 */
export type SessionVerdict =
  /** Any 2xx. The credential works right now — authoritative, beats any local read. */
  | { verdict: 'active' }
  /** 401 `missing_token`. LOCAL: I sent nothing. Says NOTHING about the session. */
  | { verdict: 'absent' }
  /** 401 `expired`. LOCAL: recoverable by renewing from the opaque session_token. */
  | { verdict: 'expired' }
  /** 401 `SESSION_NOT_ACTIVATED`. GLOBAL: recoverable by activating. */
  | { verdict: 'not_activated'; cause: NotActivatedCause }
  /** 401 bad_signature|unknown_kid|wrong_issuer. GLOBAL and terminal. */
  | { verdict: 'rejected'; cause: RejectedCause }
  /** Not an adjudication: back off, change nothing. */
  | { verdict: 'unknown'; cause: UnknownVerdictCause };

/**
 * The exact `auth_reason` vocabulary the backend can emit
 * (`go/internal/api/auth_middleware.go`, guarded closed by
 * `TestAuthReasonVocabularyIsClosed`). Anything outside this set reaches
 * `unrecognized_reason` rather than being guessed at.
 */
const AUTH_REASON = {
  missingToken: 'missing_token',
  expired: 'expired',
  badSignature: 'bad_signature',
  unknownKid: 'unknown_kid',
  wrongIssuer: 'wrong_issuer',
  missingAccountClaim: 'missing_account_claim',
  malformedTenancy: 'malformed_tenancy_claims',
  secretNotConfigured: 'secret_not_configured',
} as const;

/** The one error_code that means "activate me". */
const SESSION_NOT_ACTIVATED = 'SESSION_NOT_ACTIVATED';
/** The one error_code that means "the identity provider is degraded". */
const STYTCH_UNAVAILABLE = 'STYTCH_UNAVAILABLE';

/** The verdict every 2xx produces. Exported so callers need not build a literal. */
export const VERDICT_ACTIVE: SessionVerdict = { verdict: 'active' };

/**
 * Adjudicate a thrown SDK error into a `SessionVerdict`.
 *
 * Reads `APIError.code` (the backend's `error_code`) and `APIError.authReason`
 * (the backend's `auth_reason`). It NEVER matches message text — that is the
 * anti-pattern this whole arc exists to delete.
 *
 * A `MissingCredentialError` maps to `absent`: it is the client-side twin of
 * `401 missing_token` ("I have nothing to send"), and mapping it here means a
 * caller can funnel every failure through one classifier instead of
 * special-casing the pre-flight throw.
 *
 * Anything unrecognisable — a foreign error object, a 401 with no reason, an
 * `auth_reason` from a newer backend — reaches `unknown`. That is not a
 * fallback: `unknown` is a NAMED verdict whose entire contract is "this
 * response adjudicated nothing, change nothing", and each route to it carries a
 * distinct `cause` so it is inspectable and testable.
 */
export function classifyVerdict(err: unknown): SessionVerdict {
  // Client-side: a configured provider had no credential to send.
  if (err instanceof MissingCredentialError) {
    return { verdict: 'absent' };
  }
  if (err instanceof TimeoutError) {
    return { verdict: 'unknown', cause: 'timeout' };
  }
  if (err instanceof NetworkError || err instanceof RequestCancelledError) {
    return { verdict: 'unknown', cause: 'transport' };
  }
  if (!(err instanceof APIError)) {
    return { verdict: 'unknown', cause: 'transport' };
  }

  // The identity provider being degraded is checked before the generic 5xx arm
  // so back-off policy can distinguish "Stytch is down" from "we are down".
  if (err.code === STYTCH_UNAVAILABLE) {
    return { verdict: 'unknown', cause: 'provider_unavailable' };
  }
  if (err.status >= 500) {
    return { verdict: 'unknown', cause: 'server_error' };
  }
  if (err.status !== 401) {
    // A 403/404/409 means the credential PASSED the auth gate and the failure is
    // about something else entirely. It adjudicates nothing.
    return { verdict: 'unknown', cause: 'unadjudicated' };
  }

  // From here: a 401. The error_code carries the coarse class, auth_reason the
  // fine cause. SESSION_NOT_ACTIVATED is checked first because it is the one
  // 401 whose recovery (activate) is neither renew nor re-authenticate.
  if (err.code === SESSION_NOT_ACTIVATED) {
    return {
      verdict: 'not_activated',
      cause:
        err.authReason === AUTH_REASON.malformedTenancy
          ? 'malformed_tenancy_claims'
          : 'missing_account_claim',
    };
  }

  switch (err.authReason) {
    case AUTH_REASON.missingToken:
      return { verdict: 'absent' };
    case AUTH_REASON.expired:
      return { verdict: 'expired' };
    case AUTH_REASON.badSignature:
      return { verdict: 'rejected', cause: 'bad_signature' };
    case AUTH_REASON.unknownKid:
      return { verdict: 'rejected', cause: 'unknown_kid' };
    case AUTH_REASON.wrongIssuer:
      return { verdict: 'rejected', cause: 'wrong_issuer' };
    case AUTH_REASON.missingAccountClaim:
      // Reachable on the handler lane, which sets auth_reason but whose
      // error_code the coarse check above may not have matched.
      return { verdict: 'not_activated', cause: 'missing_account_claim' };
    case AUTH_REASON.malformedTenancy:
      return { verdict: 'not_activated', cause: 'malformed_tenancy_claims' };
    case AUTH_REASON.secretNotConfigured:
      // The SERVER cannot verify machine tokens. Nothing about the caller's
      // credential was decided.
      return { verdict: 'unknown', cause: 'server_misconfigured' };
    case null:
      return { verdict: 'unknown', cause: 'unadjudicated' };
    default:
      return { verdict: 'unknown', cause: 'unrecognized_reason' };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Layer 2 — HolderPhase
// ─────────────────────────────────────────────────────────────────────────────

/** Why a holder holds no credential. The only vocabulary `NoCredential` accepts. */
export type SignedOutReason =
  | 'never_signed_in'
  | 'user_signout'
  | 'reset'
  | 'revoked'
  | 'activation_failed'
  | 'sign_in_abandoned'
  | 'handoff_failed'
  | 'credential_rejected';

/** How a credential is being acquired. Diagnostic only — never on the wire. */
export type AcquisitionSource = 'browser_bounce' | 'handoff' | 'stytch' | 'org_create';

/**
 * The tenancy a credential resolves to. Every field is required: a holder that
 * has an identity has ALL of it, which is what makes "claims but no token" and
 * "token but no claims" unrepresentable.
 */
export interface CredentialIdentity {
  org_id: string;
  account_id: string;
  user_id: string;
  email: string;
}

/**
 * The credential itself.
 *
 * Both halves are required, and that is deliberate: `session_token` (the opaque
 * Stytch token) is the ONLY thing that can renew an expired `session_jwt`, so
 * requiring it is what makes `degraded` structurally renewable rather than
 * hopefully-renewable.
 */
export interface CredentialMaterial {
  /** The bearer JWT sent as `Authorization: Bearer …`. */
  session_jwt: string;
  /** Opaque Stytch session token — the renewal key. Never sent as a bearer. */
  session_token: string;
}

/** Why a holder is `degraded` (sdk ADR-012). */
export type DegradedReason = 'expired' | 'unconfirmed';

/** The phases a credential holder can be in (sdk ADR-012). */
export type HolderPhase =
  /** No credential at all. Reached ONLY by `rejected` or an explicit local clear. */
  | { phase: 'no_credential'; reason: SignedOutReason }
  /** A sign-in is in flight. No material yet. */
  | { phase: 'acquiring'; since: number; source: AcquisitionSource; nonce?: string }
  /** A claim-less Stytch session: real material, no tenancy. Recovery: activate. */
  | { phase: 'unactivated'; material: CredentialMaterial }
  /** Working. Identity and expiry are known and non-optional. */
  | {
      phase: 'usable';
      material: CredentialMaterial;
      identity: CredentialIdentity;
      expires_at: number;
    }
  /** Believed expired, or unconfirmable, but renewable (sdk ADR-012). */
  | {
      phase: 'degraded';
      material: CredentialMaterial;
      identity: CredentialIdentity;
      expires_at: number;
      reason: DegradedReason;
      since: number;
      attempts: number;
    };

/** The phase discriminants, as a value — the daemon frame uses the same strings. */
export type HolderPhaseName = HolderPhase['phase'];

/** The starting phase of any holder that has never held a credential. */
export const INITIAL_PHASE: HolderPhase = {
  phase: 'no_credential',
  reason: 'never_signed_in',
};

// ─────────────────────────────────────────────────────────────────────────────
// Events
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Everything that can move a holder.
 *
 * Exactly two kinds, per the derivation rule: local material events the holder
 * itself caused, and one adjudication event carrying a `SessionVerdict`.
 */
export type HolderEvent =
  // ── local material events ─────────────────────────────────────────────────
  /** The user started a sign-in. `at` is epoch millis. */
  | { type: 'SignInStarted'; at: number; source: AcquisitionSource; nonce?: string }
  /** The in-flight sign-in ended without material. */
  /** Material WITH tenancy arrived (browser bounce, handoff redeem, switch-org). */
  | {
      type: 'CredentialAcquired';
      material: CredentialMaterial;
      identity: CredentialIdentity;
      expires_at: number;
    }
  /** Material arrived, but the session carries no account claim yet. */
  | { type: 'CredentialAcquiredUnactivated'; material: CredentialMaterial }
  /** Activate injected the tenancy claims into the existing session. */
  /** Activate failed terminally. */
  | { type: 'ActivationFailed' }
  /** A renew round-trip produced fresh material for the SAME identity. */
  | { type: 'CredentialRenewed'; material: CredentialMaterial; expires_at: number }
  /**
   * A LOCAL clock read says the credential's `exp` has passed.
   *
   * This is the single most dangerous event in the machine, and its blast radius
   * is deliberately tiny: `usable -> degraded(expired)` and nothing else. It can NEVER reach
   * `NoCredential`. A local answer is not a global answer — the backend is the
   * only authority that can retire a session, and it does so via `rejected`.
   */
  | { type: 'ClockExpiry'; now: number }
  /** The user (or the app, deliberately) discarded the credential. */
  | { type: 'LocalClear'; reason: SignedOutReason }
  /**
   * A sign-out OBSERVED from another JS realm of the same browser profile —
   * the cross-tab propagation of a shared origin-scoped phase entry
   * (sdk ADR-010). Deliberately a DISTINCT variant from
   * `LocalClear`: the observing realm did not initiate the sign-out, so the
   * architecture test's "LocalClear appears in exactly one place — inside
   * signOut" stays true while the transition is identical (any phase ->
   * no_credential, always legal).
   */
  | { type: 'RemoteClear'; reason: SignedOutReason }
  // ── adjudication ──────────────────────────────────────────────────────────
  /** The backend answered. `at` is epoch millis, used to stamp `degraded`. */
  | { type: 'VerdictObserved'; verdict: SessionVerdict; at: number };

// ─────────────────────────────────────────────────────────────────────────────
// Transition result
// ─────────────────────────────────────────────────────────────────────────────

/** Why a (phase, event) pair was refused. A closed, switchable vocabulary. */
export type RefusalReason =
  /** A sign-in cannot start on a holder that already holds material. */
  | 'holder_already_has_material'
  /** Only an in-flight acquisition can fail. */
  | 'no_acquisition_in_flight'
  /** Accepting claim-less material here would DOWNGRADE an activated holder. */
  | 'would_downgrade_activated_holder'
  /** Only an unactivated holder can be activated. */
  | 'not_awaiting_activation'
  /** Renewal needs an identity to renew FOR. */
  | 'no_identity_to_renew'
  /** There is no locally-known expiry to observe. */
  | 'no_expiry_to_observe';

/** A named refusal. Never silent — the caller can log it, assert it, or throw. */
export interface TransitionRefusal {
  from: HolderPhaseName;
  event: HolderEvent['type'];
  reason: RefusalReason;
  message: string;
}

/**
 * What `applyEvent` returns.
 *
 * Three outcomes are distinguishable, which matters: an ILLEGAL pair
 * (`refusal !== null`) must never look like a legal-but-inert one
 * (`changed === false`, `refusal === null` — e.g. applying an `absent` verdict,
 * which correctly changes nothing).
 */
export interface TransitionResult {
  /** The resulting phase. On a refusal this is the UNCHANGED input phase. */
  phase: HolderPhase;
  /** True when `phase` is not referentially the input phase. */
  changed: boolean;
  /** Non-null exactly when the (phase, event) pair was illegal. */
  refusal: TransitionRefusal | null;
}

/** Compile-time exhaustiveness guard. Reaching it at runtime is a real bug. */
function assertNever(value: never, context: string): never {
  throw new Error(
    `${context}: unhandled variant ${JSON.stringify(value)} — the union grew without the reducer`,
  );
}

const REFUSAL_MESSAGES: Record<RefusalReason, string> = {
  holder_already_has_material:
    'a holder that already has material must be cleared (LocalClear) before a new sign-in starts',
  no_acquisition_in_flight: 'nothing is being acquired, so nothing can fail to be acquired',
  would_downgrade_activated_holder:
    'accepting claim-less material would discard a working tenancy; re-acquire with claims instead',
  not_awaiting_activation: 'only an unactivated holder can be activated',
  no_identity_to_renew: 'renewal preserves an identity; this holder has none to preserve',
  no_expiry_to_observe: 'this holder has no locally-known expiry, so a clock read decides nothing',
};

function refuse(
  phase: HolderPhase,
  event: HolderEvent,
  reason: RefusalReason,
): TransitionResult {
  return {
    phase,
    changed: false,
    refusal: {
      from: phase.phase,
      event: event.type,
      reason,
      message: `${event.type} is illegal from ${phase.phase}: ${REFUSAL_MESSAGES[reason]}`,
    },
  };
}

function moved(phase: HolderPhase): TransitionResult {
  return { phase, changed: true, refusal: null };
}

/** Legal, but this event carries no phase information for this holder. */
function inert(phase: HolderPhase): TransitionResult {
  return { phase, changed: false, refusal: null };
}

type RenewablePhase = Extract<HolderPhase, { phase: 'usable' | 'degraded' }>;

// `since`, `attempts` and their reset rule (sdk ADR-018).
function degrade(phase: RenewablePhase, reason: DegradedReason, at: number): HolderPhase {
  const wasDegraded = phase.phase === 'degraded';
  return {
    phase: 'degraded',
    material: phase.material,
    identity: phase.identity,
    expires_at: phase.expires_at,
    reason,
    since: wasDegraded ? phase.since : at,
    attempts:
      reason === 'expired'
        ? 0
        : wasDegraded && phase.reason === 'unconfirmed'
          ? phase.attempts + 1
          : 1,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// The reducer
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The SOLE transition function.
 *
 * Nothing else in the platform may compute a `HolderPhase`. Exhaustive over the
 * event union (`assertNever`), and every illegal (phase, event) pair returns a
 * NAMED refusal with the phase untouched — never a silent no-op, because a
 * silent no-op is how a state machine quietly stops being one.
 */
export function applyEvent(phase: HolderPhase, event: HolderEvent): TransitionResult {
  switch (event.type) {
    case 'SignInStarted':
      // Legal from no_credential (the normal start) and from acquiring (the user
      // clicked Sign In again — a restart with a fresh nonce loses nothing).
      // Refused wherever material exists: starting an acquisition would strand it.
      if (phase.phase === 'no_credential' || phase.phase === 'acquiring') {
        return moved({
          phase: 'acquiring',
          since: event.at,
          source: event.source,
          ...(event.nonce === undefined ? {} : { nonce: event.nonce }),
        });
      }
      return refuse(phase, event, 'holder_already_has_material');

    case 'CredentialAcquired':
      // Total: acquiring claim-bearing material is a local event the holder
      // itself caused, and it is legitimate from every phase — first sign-in,
      // re-sign-in over a dead session, switch-organization over a live one.
      return moved({
        phase: 'usable',
        material: event.material,
        identity: event.identity,
        expires_at: event.expires_at,
      });

    case 'CredentialAcquiredUnactivated':
      if (
        phase.phase === 'no_credential' ||
        phase.phase === 'acquiring' ||
        phase.phase === 'unactivated'
      ) {
        return moved({ phase: 'unactivated', material: event.material });
      }
      // Replacing a resolved tenancy with a claim-less session is a downgrade.
      return refuse(phase, event, 'would_downgrade_activated_holder');

    case 'ActivationFailed':
      if (phase.phase === 'unactivated') {
        return moved({ phase: 'no_credential', reason: 'activation_failed' });
      }
      return refuse(phase, event, 'not_awaiting_activation');

    case 'CredentialRenewed':
      // Renewal preserves the identity by construction — the event cannot carry
      // one, so a renew can never silently re-tenant a holder.
      if (phase.phase === 'usable' || phase.phase === 'degraded') {
        return moved({
          phase: 'usable',
          material: event.material,
          identity: phase.identity,
          expires_at: event.expires_at,
        });
      }
      return refuse(phase, event, 'no_identity_to_renew');

    case 'ClockExpiry':
      // THE constrained event. usable -> degraded(expired), and nothing else,
      // ever.
      if (phase.phase === 'usable') {
        return moved(degrade(phase, 'expired', event.now));
      }
      if (phase.phase === 'degraded') {
        if (phase.reason === 'unconfirmed') {
          return moved(degrade(phase, 'expired', event.now));
        }
        // Legal, inert: a second clock read decides nothing new.
        return inert(phase);
      }
      // no_credential | acquiring | unactivated carry no expires_at at all.
      return refuse(phase, event, 'no_expiry_to_observe');

    case 'LocalClear':
      // The only local route to no_credential, and it is always legal: the user
      // may sign out from any state.
      return moved({ phase: 'no_credential', reason: event.reason });

    case 'RemoteClear':
      // The cross-tab twin of LocalClear: another realm of this profile signed
      // out, and the shared phase entry says so. Identical transition, distinct
      // type — the observer applies, it does not clear (sdk ADR-010).
      // Legal and INERT at no_credential: a duplicate observation — the boot
      // snapshot of a signed-out daemon, or a racing sibling — must not
      // re-enter the terminal phase, because that re-runs the teardown barrier
      // and bumps the sign-out epoch that discards a later sign-in's acquire.
      if (phase.phase === 'no_credential') {
        return inert(phase);
      }
      return moved({ phase: 'no_credential', reason: event.reason });

    case 'VerdictObserved':
      return applyVerdict(phase, event.verdict, event.at);

    default:
      return assertNever(event, 'applyEvent');
  }
}

/**
 * Apply a backend adjudication.
 *
 * Never refuses: a verdict is an observation, and observing is always legal.
 * What varies is whether the observation carries phase information for THIS
 * holder — an `absent` verdict never does, for any holder, ever.
 */
function applyVerdict(
  phase: HolderPhase,
  verdict: SessionVerdict,
  at: number,
): TransitionResult {
  switch (verdict.verdict) {
    case 'active':
      // A 2xx is authoritative and beats any local read (sdk ADR-010).
      if (phase.phase === 'degraded') {
        return moved({
          phase: 'usable',
          material: phase.material,
          identity: phase.identity,
          expires_at: phase.expires_at,
        });
      }
      // usable stays usable; no_credential / acquiring / unactivated learn
      // nothing — the 2xx came from an unauthenticated or activate-lane route.
      return inert(phase);

    case 'absent':
      // 401 missing_token. THE rule: this is LOCAL and adjudicates nothing.
      // It changes no phase, from any phase, ever. Sending nothing and being
      // told so is not evidence that the session is dead — conflating the two
      // is the exact bug that produced an infinite sign-in loop.
      return inert(phase);

    case 'expired':
      // LOCAL and recoverable by renew.
      if (phase.phase === 'usable') {
        return moved(degrade(phase, 'expired', at));
      }
      if (phase.phase === 'degraded' && phase.reason === 'unconfirmed') {
        return moved(degrade(phase, 'expired', at));
      }
      // An unactivated holder has no identity to carry into degraded, and
      // none of the rest may be escalated to signed-out.
      return inert(phase);

    case 'not_activated':
      // GLOBAL: the backend says this session carries no usable account claim.
      // A holder that believed it had a tenancy is wrong; demote to the phase
      // whose recovery is `activate`. Material is preserved — this is not a
      // sign-out.
      if (phase.phase === 'usable' || phase.phase === 'degraded') {
        return moved({ phase: 'unactivated', material: phase.material });
      }
      return inert(phase);

    case 'rejected':
      // GLOBAL and terminal — the ONLY verdict that may reach no_credential.
      if (
        phase.phase === 'usable' ||
        phase.phase === 'degraded' ||
        phase.phase === 'unactivated'
      ) {
        return moved({ phase: 'no_credential', reason: 'credential_rejected' });
      }
      // acquiring: the rejected credential is not this holder's — a sign-in is
      // already the recovery, so tearing it down would be counterproductive.
      // no_credential: already there; do not overwrite a more specific reason
      // (`user_signout`) with a less specific one.
      return inert(phase);

    case 'unknown':
      // `unadjudicated` says NOTHING about the credential — the request passed
      // the auth gate and failed for an unrelated reason (403/404/409/429). It
      // is not an auth signal at all, so it must leave the phase alone.
      //
      // This is not a nicety. `classifyVerdict` funnels every failure through
      // one classifier, so without this arm a routine 404 on a deleted
      // workspace knocks a healthy holder out of `usable`: a gate rendering on
      // `phase === 'usable'` flips to a reconnecting screen, and `attempts`
      // climbs monotonically across unrelated 404s because only a 2xx resets
      // it — a back-off against a session nothing adjudicated. The comment on
      // the classifier arm already said "it adjudicates nothing"; the reducer
      // now agrees with it.
      //
      // The genuinely unconfirmable causes (5xx, provider unavailable,
      // transport) DO park the holder below, retaining material and identity.
      // That is the whole point of the phase: "I believe I am signed in and
      // cannot currently confirm it" is a real state, and its absence is why a
      // provider blip used to sign surfaces out.
      if (verdict.cause === 'unadjudicated') {
        return inert(phase);
      }
      // Park, preserving everything, and count the attempt so a caller can
      // back off. A holder with no material has nothing to park.
      if (phase.phase === 'usable' || phase.phase === 'degraded') {
        return moved(degrade(phase, 'unconfirmed', at));
      }
      return inert(phase);

    default:
      return assertNever(verdict, 'applyVerdict');
  }
}

/**
 * Convenience: classify a thrown error and apply it in one step.
 *
 * The two halves stay separately exported because they are separately testable
 * and separately useful — a caller with a 2xx applies `VERDICT_ACTIVE` directly
 * without an error to classify.
 */
export function applyError(
  phase: HolderPhase,
  err: unknown,
  at: number,
): TransitionResult {
  return applyEvent(phase, {
    type: 'VerdictObserved',
    verdict: classifyVerdict(err),
    at,
  });
}
